// @flow

import * as React from "react";
import chunk from "lodash/chunk";
import cx from "classnames";
import Popover from "../../Popover";
import TextInput from "../../Input/TextInput";
import DatePickersLogic from "../shared/DatePickersLogic";
import PickerNav from "../shared/PickerNav";
import { isMobile, isIPhone, isIPad } from "../../../utils/deviceDetection";
import NativeMonthPicker from "../NativeMonthPicker";

import type { InputType } from "../../Input/TextInput";
import type { MonthsPropsType } from "../shared/DatePickersLogic";

import styles from "./MonthPicker.scss";
import {
  monthPickerDisplayFormat as monthPickerDisplayFormatDefault,
  monthPickerFormFormat as monthPickerFormFormatDefault
} from "../../../utils/formats";

type MonthPickerProps = {
  input: InputType,
  startDate: Date,
  minDate: Date,
  maxDate: Date,
  placeholder: string,
  monthPickerDisplayFormat: string,
  monthPickerFormFormat: string
};

const renderMonths = (monthsProps: MonthsPropsType, closePopover: () => void) => (
  <div className={styles.monthsWrapper}>
    {chunk(monthsProps, 3).map((row: MonthsPropsType, rowIndex: number) => (
      // eslint-disable-next-line react/no-array-index-key
      <div className={styles.monthsRow} key={`row${rowIndex}`}>
        {row.map(({ name, onClick, isSelectable, isSelected }, indexInRow) => {
          const originalIndex = rowIndex * 3 + indexInRow;

          return (
            <button
              type="button"
              className={cx(styles.month, {
                [styles.disabledMonth]: !isSelectable,
                [styles.selectedMonth]: isSelected
              })}
              onClick={isSelectable ? () => onClick(closePopover) : undefined}
              key={name}
              data-closeontab={originalIndex + 1 === monthsProps.length}
              data-testId={`month:${originalIndex + 1}`}
            >
              {name}
            </button>
          );
        })}
      </div>
    ))}
  </div>
);

const MonthPicker = ({
  input,
  startDate,
  minDate,
  maxDate,
  placeholder,
  monthPickerDisplayFormat,
  monthPickerFormFormat
}: MonthPickerProps) =>
  isMobile() && !isIPhone() && !isIPad() ? (
    <NativeMonthPicker
      input={input}
      minDate={minDate}
      maxDate={maxDate}
      placeholder={placeholder}
    />
  ) : (
    <DatePickersLogic
      input={input}
      startDate={startDate}
      minDate={minDate}
      maxDate={maxDate}
      pickerType="month"
      monthPickerDisplayFormat={monthPickerDisplayFormat}
      monthPickerFormFormat={monthPickerFormFormat}
    >
      {({
        yearNavProps: {
          label,
          setPreviousYear,
          setNextYear,
          isNextYearSelectable,
          isPreviousYearSelectable,
          ariaLabelPrevious,
          ariaLabelNext
        },
        monthPickerProps: { monthsProps, monthPickerValue, onOpenMonthPicker },
        shared: { onKeyDown }
      }) => (
        <Popover
          input={({ onFocus }) => (
            <TextInput
              input={{
                ...input,
                onFocus: e => {
                  onFocus(e);
                  input.onFocus(e);
                },
                value: monthPickerValue
              }}
              placeholder={placeholder}
              readOnly
              onKeyDown={onKeyDown}
              data-closeonshifttab
              data-testid={`monthInput:${input.name}`}
              sideSymbol={() => <i className="icon-calendar-empty" />}
            />
          )}
          onOpen={onOpenMonthPicker}
          content={({ closePopover }) => (
            <div className={styles.popperWrapper}>
              <PickerNav
                label={label}
                setPrevious={setPreviousYear}
                setNext={setNextYear}
                isPreviousArrowSelectable={isPreviousYearSelectable}
                isNextArrowSelectable={isNextYearSelectable}
                ariaLabelPrevious={ariaLabelPrevious}
                ariaLabelNext={ariaLabelNext}
              />
              {renderMonths(monthsProps, closePopover)}
            </div>
          )}
        />
      )}
    </DatePickersLogic>
  );

MonthPicker.defaultProps = {
  startDate: new Date(),
  minDate: new Date(0, 0, 1),
  maxDate: new Date(2999, 11, 31),
  placeholder: "",
  monthPickerDisplayFormat: monthPickerDisplayFormatDefault,
  monthPickerFormFormat: monthPickerFormFormatDefault
};

export default MonthPicker;
